
import { AccountSummary } from "@/store/Account/AccountInterface";
import {
  initializeTree,
  searchUser,
  setRootUsername,
  refreshTree,
  doGetUserTreeBatch,
  CLEAR_PENDING_FLAG,
} from "@/store/UserNetwork/UserNetworkInterfaces";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import BinaryNode from "../components/BinaryNode.vue";
import { BinaryTreeNodeUser } from "functions/src/Users/UsersInterfaces";

export default defineComponent({
  components: { BinaryNode },
  name: "binary-network",
  data() {
    return {
      leftLink: "",
      rightLink: "",
      searchUsernameString: "",
      loading: true,
      invalidUser: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["getUsernamePK", "getUserProfile"]),
    ...mapGetters("userNetwork", {
      rootUserNode: "getRootUserNode",
    }),
    ...mapGetters("auth", ["getUsernamePK"]),
    ...mapGetters("account", ["getAccountSummary"]),
    loggedUserEnroller(): string {
      return this.getUserProfile.enroller;
    },
    accountSummary(): AccountSummary {
      return this.getAccountSummary;
    },
    linkBase(): string {
      const hrefArray = window.location.href.split("/");
      return hrefArray[0] + "//" + hrefArray[2] + "/registro/";
    },
    totalRightPoints(): number {
      return (
        this.rootUserNode?.points.right.pending +
          this.rootUserNode?.points.right.paid || 0
      );
    },
    totalLeftPoints(): number {
      return (
        this.rootUserNode?.points.left.pending +
          this.rootUserNode?.points.left.paid || 0
      );
    },
    totalPoints(): number {
      return this.totalRightPoints + this.totalLeftPoints || 0;
    },
    binarySide(): string {
      return this.accountSummary?.binarySide
        ? this.accountSummary?.binarySide
        : "";
    },
    innerLegUnlocked(): boolean {
      // undefined = NOT PLACED = LOCKED. A member with no binaryTree node has no
      // power line to open. Defaulting to `true` here silently unlocked the inner
      // leg for every registered-not-yet-activated member (#559).
      if (this.accountSummary?.innerLegUnlocked == undefined) {
        return false;
      }
      return this.accountSummary?.innerLegUnlocked
        ? this.accountSummary?.innerLegUnlocked
        : false;
    },
  },
  methods: {
    async initializeTree() {
      this.loading = true;
      await this.$store
        .dispatch("userNetwork/" + initializeTree)
        .finally(() => {
          this.loading = false;
        });
    },
    async changeTreeRoot(newUsername: string) {
      if (newUsername != undefined) {
        this.loading = true;
        await this.$store
          .dispatch("userNetwork/" + setRootUsername, newUsername)
          .catch((error) => {
            if (
              error.name &&
              (error.name == "USER_NOT_FOUND" ||
                error.name == "USER_NOT_AUTHORIZED")
            ) {
              this.invalidUser = true;
            } else {
              alert(error);
            }
          })
          .finally(async () => {
            // Ensure pending node updates complete before UI updates and components mount
            await this.updatePendingVisibleNodes();
            this.loading = false;
          });
      }
    },
    copyToClipboard(side: string) {
      const el = document.getElementById(
        "referralUrl_" + side
      ) as HTMLInputElement;
      el.select();
      el.setSelectionRange(0, 99999);
      document.execCommand("copy");
    },
    sideUnlocked(btnSide: "right" | "left"): boolean {
      // THE RULE, stated once: while the inner leg is locked, only the POWER leg
      // may be recruited into. Written as two `binarySide == "left"/"right"`
      // branches, an UNKNOWN side (the computed collapses undefined to "")
      // matched neither and both legs were offered — the lock silently opened in
      // exactly the state where we know least. This form fails CLOSED: an
      // unknown side equals no side, so nothing is offered.
      if (this.innerLegUnlocked) {
        return true;
      }
      return btnSide === this.binarySide;
    },
    async searchUser() {
      this.loading = true;
      await this.$store
        .dispatch(
          "userNetwork/" + searchUser,
          this.searchUsernameString.toLowerCase()
        )
        .catch((error) => {
          if (
            error.name &&
            (error.name == "USER_NOT_FOUND" ||
              error.name == "USER_NOT_AUTHORIZED")
          ) {
            this.invalidUser = true;
          } else {
            alert(error);
          }
        })
        .finally(async () => {
          // Ensure pending node updates complete before UI updates and components mount
          await this.updatePendingVisibleNodes();
          this.loading = false;
        });
    },

    /**
     * Collects the usernames of all nodes that are considered "visible" in the
     * binary network tree starting from the given node.
     *
     * A node is treated as visible if it lies within a fixed maximum depth
     * (`maxLevel`, currently 4) from the starting node. Children can be stored
     * either as usernames (string) or as full node objects; this method resolves
     * them via the `userNetwork/getTreeForUser` getter before recursing.
     *
     * @param node - The root node from which to start collecting visible nodes.
     *               Expected to contain a `username` and optional `childLeft` /
     *               `childRight` properties.
     * @param level - The current depth in the tree traversal, starting at 1 for
     *                the initial `node`. This is used internally to stop
     *                recursion once `maxLevel` is exceeded.
     * @returns An array of usernames representing all nodes that are visible
     *          (up to `maxLevel` levels deep) from the starting node.
     */
    collectVisibleNodes(
      node: BinaryTreeNodeUser | null,
      level: number = 1
    ): string[] {
      const maxLevel = 4;
      if (!node || !node.username || level > maxLevel) {
        return [];
      }

      const visibleNodes = [node.username];

      if (level < maxLevel) {
        // Recursively collect children
        if (node.childLeft) {
          const leftUsername =
            typeof node.childLeft === "string"
              ? node.childLeft
              : node.childLeft.username;
          const leftNode =
            this.$store.getters["userNetwork/getTreeForUser"](leftUsername);
          if (leftNode && leftNode.username) {
            visibleNodes.push(...this.collectVisibleNodes(leftNode, level + 1));
          }
        }
        if (node.childRight) {
          const rightUsername =
            typeof node.childRight === "string"
              ? node.childRight
              : node.childRight.username;
          const rightNode =
            this.$store.getters["userNetwork/getTreeForUser"](rightUsername);
          if (rightNode && rightNode.username) {
            visibleNodes.push(
              ...this.collectVisibleNodes(rightNode, level + 1)
            );
          }
        }
      }

      return visibleNodes;
    },
    async refreshVisibleNodes() {
      if (!this.rootUserNode) return;

      this.loading = true;
      try {
        const visibleUsernames = [
          ...new Set(this.collectVisibleNodes(this.rootUserNode)),
        ];
        await this.$store.dispatch(
          "userNetwork/" + refreshTree,
          visibleUsernames
        );
      } catch (error) {
        console.error("Error refreshing tree:", error);
      } finally {
        this.loading = false;
      }
    },
    async updatePendingVisibleNodes() {
      if (!this.rootUserNode) return;

      const visibleUsernames = [
        ...new Set(this.collectVisibleNodes(this.rootUserNode)),
      ];
      const pendingNodes = visibleUsernames.filter((username) =>
        this.$store.getters["userNetwork/isNodePendingUpdate"](username)
      );

      if (pendingNodes.length > 0) {
        // Update all pending visible nodes using batch API
        try {
          await this.$store.dispatch("userNetwork/" + doGetUserTreeBatch, {
            usernames: pendingNodes,
            levels: 1,
          });
          // Clear pending flags for all successfully updated nodes
          pendingNodes.forEach((username) => {
            this.$store.commit("userNetwork/" + CLEAR_PENDING_FLAG, username);
          });
        } catch (error) {
          console.error("Error updating pending nodes:", error);
        }
      }
    },
  },
  async mounted() {
    if (this.rootUserNode == "" || this.rootUserNode == undefined) {
      // A NOT PLACED member has no tree, and the store throws NOT_PLACED (§17) —
      // a normal, majority day-1 state, not a reason to abandon mounted(). The
      // bare `await` used to reject here and the two lines below never ran, so
      // the member's REFERRAL LINKS came out empty. (The empty-state screen for
      // the tree itself is #574.)
      await this.initializeTree().catch((e) => {
        console.error("No se pudo cargar el árbol binario:", e);
      });
    } else {
      this.loading = false;
    }
    // Built from the member's OWN username, never from the tree root: the links
    // must not depend on the tree having loaded, and `rootUserNode` is undefined
    // for anyone who is not placed.
    this.leftLink = this.linkBase + this.getUsernamePK + "/izq";
    this.rightLink = this.linkBase + this.getUsernamePK + "/der";
  },
  watch: {
    searchUsernameString() {
      this.invalidUser = false;
    },
  },
});
